import { useCallback, useEffect, useRef, useState } from "react"
import { useAuth } from "@/hooks/use-auth"
import type { AttributionDateRange } from "./types"

export interface ProductDailyPoint {
  date: string
  product_id: number
  product_title: string
  revenue: number
  orders: number
}

export interface UseProductDailySeriesResult {
  series: ProductDailyPoint[]
  isLoading: boolean
}

interface UseProductDailySeriesOptions {
  integrationId: string
  dateRange: AttributionDateRange
  productIds: number[]
  minimumOrderTotal: number
}

export function useProductDailySeries({
  integrationId,
  dateRange,
  productIds,
  minimumOrderTotal,
}: UseProductDailySeriesOptions): UseProductDailySeriesResult {
  const { fetchWithAuth, isAuthenticated } = useAuth()
  const [series, setSeries] = useState<ProductDailyPoint[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const productIdsKey = productIds.join(",")
  const fetchRef = useRef(0)

  const refresh = useCallback(async () => {
    if (!integrationId || !isAuthenticated || productIds.length === 0) {
      setSeries([])
      setIsLoading(false)
      return
    }

    const fetchId = ++fetchRef.current
    setIsLoading(true)

    try {
      const params = new URLSearchParams({
        integration_id: integrationId,
        month_start_utc: dateRange.month_start_utc.toISOString(),
        month_end_utc: dateRange.month_end_utc.toISOString(),
        timezone: dateRange.timezone,
        minimum_order_total: Number.isFinite(minimumOrderTotal) ? String(minimumOrderTotal) : "0",
        product_ids: productIdsKey,
        breakdown_by_product: "true",
      })

      const res = await fetchWithAuth(
        `/api/integrations/shopify/attribution-stats/daily?${params}`,
      )

      if (fetchId !== fetchRef.current) return

      if (res.ok) {
        const data = await res.json()
        setSeries(data.series ?? [])
      } else {
        setSeries([])
      }
    } catch (error) {
      console.error("[ProductDailySeries] Error:", error)
      if (fetchId === fetchRef.current) {
        setSeries([])
      }
    } finally {
      if (fetchId === fetchRef.current) {
        setIsLoading(false)
      }
    }
  }, [integrationId, isAuthenticated, dateRange, minimumOrderTotal, productIdsKey, fetchWithAuth, productIds.length])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { series, isLoading }
}
